// src/components/desk/ContactOptOutPanel.jsx — T27.
//
// ⚠️ ITS OWN FILE ON PURPOSE. It lived inside ManualPropertyModal for one commit, and that file
// pulls leaflet in transitively — so any harness mounting it dies on `document is not defined`
// before rendering a thing. A control whose entire correctness claim is "it is still there after
// you use it" has to be renderable in a check, so it lives where it can be.
import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { btn } from './primitives.jsx'
import { getPropertyOptouts, setPropertyOptout, getPropertyShareContacts } from '../../db/core.js'

// ── T27: "do not list me as a contact for this flat" ────────────────────────
//
// OWNER (OWNER_REVIEW_2026-08-25 §8): "make agent able to stop working on any property. Even
// their own added to system — so then when manager or admin shares it, they won't be included in
// contacts. (That's the way even when we'll have our own public site…)"
//
// 🔴 CONTACTS ONLY. It removes the agent from contact lists — the FB share number picker, the
// future public site. It does NOT touch representation, so their access to a masked property,
// their place on a meeting and their share of the commission are untouched. An agent stepping
// back from being phoned is not resigning from the deal, and the copy says so out loud, because
// a control that might silently cost someone money will not be pressed.
//
// ⚠️ ALWAYS RENDERED, opted out or not. §8 names the trap and this codebase has hit it three
// times: a control gated on its own filtered output erases itself — the moment you opt out, the
// button that would let you opt back in disappears with you. So the panel keys on the caller's
// own state from the server (`i_am_opted_out`), never on whether they appear in a filtered list.
export function ContactOptOutPanel({ propertyId, agentId, canManage = false, showSelf = true }) {
  const [state, setState] = useState(null)      // { i_am_opted_out, opted_out: [...] }
  const [roster, setRoster] = useState(null)    // §8 OPEN-22 — only fetched for management
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    if (!propertyId || (!agentId && !canManage)) return
    let gone = false
    // Management also needs the CONTACTABLE list, because "opt someone out" is meaningless
    // without the names — and that list is the same one the share picker uses, already filtered
    // by the server. Asking for it here is what keeps the two surfaces agreeing about who is on
    // this flat; deriving a second list would be a second answer to the same question.
    Promise.all([
      getPropertyOptouts(propertyId, agentId),
      canManage ? getPropertyShareContacts(propertyId).catch(() => ({ contacts: [] })) : Promise.resolve(null),
    ])
      .then(([d, r]) => { if (!gone) { setState(d); setRoster(r ? (r.contacts || []) : null); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, agentId, canManage, refresh])

  // ⚠️ NOT `if (!agentId)`. An ADMIN HAS NO ROSTER ROW (§7 OPEN-17), so `myAgentId` is null for
  // them — and keying the whole panel on it made the management half invisible to exactly the
  // people it was built for. Measured in the browser: admin Gigi opened a property and the panel
  // rendered nothing at all. The server resolves the caller from the SESSION when no agent id is
  // sent (callerParty's admin fallback), so an admin is identifiable without one.
  if (!showSelf && !canManage) return null

  async function toggle(out, targetPartyId = null) {
    setBusy(true)
    try {
      await setPropertyOptout(propertyId, agentId, { retract: out, targetPartyId })
      setRefresh(r => r + 1); setErr(null)
    }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }
  return <ContactOptOutView state={state} roster={roster} showSelf={showSelf} err={err} busy={busy} onToggle={toggle} />
}

// ⚠️ THE MARKUP IS SPLIT OUT SO IT CAN BE PROVEN. renderToStaticMarkup does NOT run effects, so
// a harness pointed at the component above renders its INITIAL state forever — and the initial
// state happens to look exactly like "you are listed", which is the answer three of my first
// assertions were passing on. They agreed with a render that had never seen the server. Anything
// that must be true in BOTH states has to be reachable without a fetch.
export function ContactOptOutView({ state, roster = null, showSelf = true, err = null, busy = false, onToggle = () => {} }) {
  const out = !!state?.i_am_opted_out
  // Colleagues who have stepped back. Shown because a manager picking contacts needs to know the
  // list is short BY CHOICE rather than by accident — an unexplained absence reads as a bug and
  // gets "fixed" by someone adding the number back by hand.
  const others = (state?.opted_out || []).filter(x => x.agent_party_id !== state?.me_party_id)

  return (
    // ⚠️ A PLAIN SECTION, NOT A <Facet>. Two reasons, and the second is the load-bearing one:
    // Facet lives in ManualPropertyModal, which drags leaflet in and makes this unrenderable in a
    // harness — and a Facet is COLLAPSIBLE. A control whose whole job is to still be findable
    // after you have used it must not be one default-collapsed panel away from invisible. That is
    // §8's self-erasing-control trap wearing different clothes.
    <section className="rounded-lg border border-border p-3">
      <h4 className="text-xs font-medium mb-1">{t('desk_optout_facet_title')}</h4>
      {/* ⚠️ Only for a viewer this flat can actually list. For anyone else the sentence would be
          a claim about them that may simply be false, and the button under it a control over
          something they were never part of. Management still gets the list below. */}
      {showSelf && (<>
        <p className="text-xs text-muted-foreground">
          {out ? t('desk_optout_state_out') : t('desk_optout_state_in')}
        </p>
        <button type="button" className={`${btn('ghost')} mt-2`} disabled={busy || !state}
          data-optout-toggle={out ? 'in' : 'out'} onClick={() => onToggle(out)}>
          {out ? t('desk_optout_action_rejoin') : t('desk_optout_action_leave')}
        </button>
        {/* The reassurance is part of the control, not decoration: without it the honest reading
            of "stop being a contact" is "stop working on this", a much bigger act. */}
        <p className="text-[11px] text-muted-foreground/80 mt-1">{t('desk_optout_scope_note')}</p>
      </>)}
      {/* §8 OPEN-22: management may step a colleague back — the obvious case is someone who has
          left, whose number should stop being published without waiting for them to log in.
          ⚠️ It is a DIFFERENT act and the record keeps it that way (acted_by_party_id), so this
          list says who is affected by name rather than offering a bulk switch.
          ⚠️ Rendered only when `roster` was fetched, i.e. for management. A non-privileged
          viewer sees the personal control above and nothing else — the server refuses them
          anyway, and a visible control that always 403s is a lock with nothing behind it. */}
      {roster && (
        <div className="mt-3 border-t border-border pt-2 space-y-1">
          <p className="text-[11px] text-muted-foreground">{t('desk_optout_manage_title')}</p>
          {roster.filter(c => c.party_id !== state?.me_party_id).map(c => {
            const isOut = (state?.opted_out || []).some(x => x.agent_party_id === c.party_id)
            return (
              <div key={c.party_id} className="flex items-center gap-2 text-xs">
                <span className={isOut ? 'line-through text-muted-foreground' : ''}>{c.name || '—'}</span>
                <button type="button" className={btn('ghost')} disabled={busy}
                  data-manage-toggle={c.party_id}
                  onClick={() => onToggle(isOut, c.party_id)}>
                  {isOut ? t('desk_optout_action_rejoin_other') : t('desk_optout_action_leave_other')}
                </button>
              </div>
            )
          })}
          {/* An opted-out colleague is NOT in the roster — the share-contacts route removes them
              at the source, which is the whole point of T27. They would therefore be unreachable
              for a restore, so they are listed here from the opt-out record instead. Without
              this, management can take someone off and nobody can put them back. */}
          {(state?.opted_out || []).filter(o => o.agent_party_id !== state?.me_party_id &&
              !roster.some(c => c.party_id === o.agent_party_id)).map(o => (
            <div key={o.agent_party_id} className="flex items-center gap-2 text-xs">
              <span className="line-through text-muted-foreground">{o.agent_party_id.slice(0, 8)}</span>
              <button type="button" className={btn('ghost')} disabled={busy}
                data-manage-toggle={o.agent_party_id}
                onClick={() => onToggle(true, o.agent_party_id)}>
                {t('desk_optout_action_rejoin_other')}
              </button>
            </div>
          ))}
        </div>
      )}
      {!roster && others.length > 0 && (
        <p className="text-[11px] text-muted-foreground/80 mt-2">
          {tLog('desk_optout_others', { n: others.length })}
        </p>
      )}
      {err && <p className="text-xs text-destructive mt-1"><Icon name="alert-triangle" /> {err}</p>}
    </section>
  )
}

